import { LegendListProps, type ColumnWrapperStyle, type LegendListRef } from '@legendapp/list';
import { AnimatedLegendList } from '@legendapp/list/reanimated';
import React, { useCallback, useMemo } from 'react';
import {
  NativeScrollEvent,
  NativeSyntheticEvent,
  ScrollView,
  StyleSheet,
  View,
  type ScrollViewProps,
  type StyleProp,
  type ViewStyle,
} from 'react-native';

import { Skeleton } from '@/components/ui/skeleton';
import { useScaling } from '@/hooks';
import { useInitState } from '@/hooks/use-init-state';
import type { BoxStyleOptions } from '../types';
import { buildBoxStyle } from '../utils';

export type GridListProps<T> = Omit<BoxStyleOptions, 'gap' | 'gapX' | 'gapY'> & {
  data: T[];
  renderItem: (info: { item: T; index: number }) => React.ReactNode;
  keyExtractor?: (item: T, index: number) => string;
  /** Forces list items to re-render when changed. */
  extraData?: LegendListProps['extraData'];
  /** Responsive column count. Accepts a number or `{ phone, tablet }`. Defaults to 1. */
  numColumns?: number | { phone: number; tablet: number };
  /** Uniform spacing between items on both axes */
  gap?: number;
  /** Horizontal spacing between columns (overrides `gap`) */
  gapX?: number;
  /** Vertical spacing between rows (overrides `gap`) */
  gapY?: number;
  style?: StyleProp<ViewStyle>;
  contentContainerStyle?: StyleProp<ViewStyle>;
  ListHeaderComponent?: React.ComponentType<any> | React.ReactElement | null;
  ListFooterComponent?: React.ComponentType<any> | React.ReactElement | null;
  ListEmptyComponent?: React.ComponentType<any> | React.ReactElement | null;
  ItemSeparatorComponent?: React.ComponentType<any> | null;
  onEndReached?: (() => void) | null;
  onEndReachedThreshold?: number;
  estimatedItemSize?: number;
  recycleItems?: boolean;
  showsVerticalScrollIndicator?: boolean;
  bounces?: boolean;
  refreshControl?: LegendListProps['refreshControl'];
  onScroll?: (event: NativeSyntheticEvent<NativeScrollEvent>) => void;
  scrollEventThrottle?: number;
  maintainVisibleContentPosition?: boolean;
  /** iOS: pass through to underlying scroll view (e.g. `'never'` to match Android padding behavior). */
  contentInsetAdjustmentBehavior?: ScrollViewProps['contentInsetAdjustmentBehavior'];
  waitForInitialLayout?: LegendListProps['waitForInitialLayout'];
  drawDistance?: LegendListProps['drawDistance'];
  enableLazyLoadingList?: boolean;
};

type GridListComponent = <T>(
  props: GridListProps<T> & React.RefAttributes<LegendListRef>,
) => React.ReactElement;

const GridListInner = <T,>(
  {
    drawDistance,
    data,
    renderItem,
    keyExtractor,
    extraData,
    numColumns: numColumnsProp = 1,
    gap: gapProp,
    gapX: gapXProp,
    gapY: gapYProp,
    style,
    contentContainerStyle,
    ItemSeparatorComponent: ItemSeparatorProp,
    padding,
    paddingX,
    paddingY,
    paddingTop,
    paddingBottom,
    paddingLeft,
    paddingRight,
    margin,
    marginX,
    marginY,
    marginTop,
    marginBottom,
    marginLeft,
    marginRight,
    flex,
    flexGrow,
    flexShrink,
    flexBasis,
    backgroundColor,
    width,
    height,
    minWidth,
    minHeight,
    maxWidth,
    maxHeight,
    overflow,
    borderRadius,
    borderWidth,
    borderColor,
    borderTopLeftRadius,
    borderTopRightRadius,
    borderBottomLeftRadius,
    borderBottomRightRadius,
    position,
    zIndex,
    bottom,
    left,
    right,
    top,
    maintainVisibleContentPosition,
    enableLazyLoadingList,
    ...rest
  }: GridListProps<T>,
  ref: React.ForwardedRef<LegendListRef>,
) => {
  const { isTablet } = useScaling();

  const { isInitialRender } = useInitState();

  const cols = useMemo(
    () =>
      typeof numColumnsProp === 'object'
        ? isTablet
          ? numColumnsProp.tablet
          : numColumnsProp.phone
        : numColumnsProp,
    [numColumnsProp, isTablet],
  );

  const hGap = gapXProp ?? gapProp ?? 0;
  const vGap = gapYProp ?? gapProp ?? 0;
  const isGrid = cols > 1;

  const outerStyle = useMemo(
    () =>
      buildBoxStyle({
        margin,
        marginX,
        marginY,
        marginTop,
        marginBottom,
        marginLeft,
        marginRight,
        flex,
        flexGrow,
        flexShrink,
        flexBasis,
        backgroundColor,
        width,
        height,
        minWidth,
        minHeight,
        maxWidth,
        maxHeight,
        overflow,
        borderRadius,
        borderWidth,
        borderColor,
        borderTopLeftRadius,
        borderTopRightRadius,
        borderBottomLeftRadius,
        borderBottomRightRadius,
        position,
        zIndex,
        bottom,
        left,
        right,
        top,
      }),
    [
      margin,
      marginX,
      marginY,
      marginTop,
      marginBottom,
      marginLeft,
      marginRight,
      flex,
      flexGrow,
      flexShrink,
      flexBasis,
      backgroundColor,
      width,
      height,
      minWidth,
      minHeight,
      maxWidth,
      maxHeight,
      overflow,
      borderRadius,
      borderWidth,
      borderColor,
      borderTopLeftRadius,
      borderTopRightRadius,
      borderBottomLeftRadius,
      borderBottomRightRadius,
      position,
      zIndex,
      bottom,
      left,
      right,
      top,
    ],
  );

  const innerStyle = useMemo(
    () =>
      buildBoxStyle({
        padding,
        paddingX,
        paddingY,
        paddingTop,
        paddingBottom,
        paddingLeft,
        paddingRight,
      }),
    [padding, paddingX, paddingY, paddingTop, paddingBottom, paddingLeft, paddingRight],
  );

  const columnWrapperStyle = useMemo<ColumnWrapperStyle | undefined>(
    () => (isGrid ? { gap: hGap } : undefined),
    [isGrid, hGap],
  );

  const Separator = useMemo(() => {
    if (ItemSeparatorProp) return ItemSeparatorProp;
    if (vGap <= 0) return undefined;
    return () => <View style={{ height: vGap / 2 }} />;
  }, [ItemSeparatorProp, vGap]);

  const gridRenderItem = useCallback(
    (info: { item: T; index: number }) => {
      const element = renderItem(info);
      if (isGrid) {
        return <View style={FLEX_ITEM}>{element}</View>;
      }
      return element;
    },
    [renderItem, isGrid],
  );

  if (isInitialRender && enableLazyLoadingList) {
    const skeletonRows = isGrid ? 4 : 12;
    const totalSkeletons = skeletonRows * cols;

    return (
      <ScrollView
        showsVerticalScrollIndicator={false}
        style={[styles.skeletonContainer, outerStyle, style] as StyleProp<ViewStyle>}
        contentContainerStyle={[innerStyle, contentContainerStyle] as StyleProp<ViewStyle>}>
        <Skeleton.Group gap={vGap || 8}>
          {isGrid
            ? Array.from({ length: skeletonRows }).map((_, rowIdx) => (
                <View key={rowIdx} style={[SKELETON_ROW, { gap: hGap }]}>
                  {Array.from({ length: cols }).map((__, colIdx) => (
                    <View key={colIdx} style={FLEX_ITEM}>
                      <Skeleton.PlayerCard />
                    </View>
                  ))}
                </View>
              ))
            : Array.from({ length: totalSkeletons }).map((_, index) => (
                <Skeleton.PlayerCard key={index} />
              ))}
        </Skeleton.Group>
      </ScrollView>
    );
  }

  return (
    <AnimatedLegendList
      ref={ref}
      data={data}
      drawDistance={drawDistance}
      renderItem={gridRenderItem}
      keyExtractor={keyExtractor}
      extraData={extraData}
      numColumns={cols}
      columnWrapperStyle={columnWrapperStyle}
      ItemSeparatorComponent={Separator}
      style={[outerStyle, style] as StyleProp<ViewStyle>}
      contentContainerStyle={[innerStyle, contentContainerStyle] as StyleProp<ViewStyle>}
      maintainVisibleContentPosition={maintainVisibleContentPosition}
      {...rest}
    />
  );
};

export const GridList = React.forwardRef(GridListInner) as GridListComponent;

const FLEX_ITEM: ViewStyle = { flex: 1 };
const SKELETON_ROW: ViewStyle = { flexDirection: 'row' };
const styles = StyleSheet.create({
  skeletonContainer: {
    marginHorizontal: 16,
  },
});
